import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Leaf, Target, Users, Database, Star, Mail, Phone, MapPin } from 'lucide-react';
import SharedNavbar from './SharedNavbar';

interface AboutUsProps {
  language?: string;
}

const AboutUs: React.FC<AboutUsProps> = ({ language = 'lt' }) => {
  const [counters, setCounters] = useState({
    users: 0,
    plants: 0,
    database: 0,
    rating: 0,
  });

  // Fix auto-scroll issue
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Animated counter effect
  useEffect(() => {
    const animateCounters = () => {
      const targets = {
        users: 2000,
        plants: 50000,
        database: 1000,
        rating: 4.9,
      };

      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;

        setCounters({
          users: Math.floor(targets.users * progress),
          plants: Math.floor(targets.plants * progress),
          database: Math.floor(targets.database * progress),
          rating: Math.floor(targets.rating * progress * 10) / 10,
        });

        if (step >= steps) {
          clearInterval(timer);
          setCounters(targets);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCounters();
          observer.disconnect();
        }
      });
    });

    const statsSection = document.getElementById("stats-section");
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SharedNavbar />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-green-50 pt-16">
        {/* Hero Section - matching landing page style */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">Apie Kiemo Draugą</h1>
            <p className="text-xl text-gray-600 max-w-2xl">Jūsų patikimas partneris augalų priežiūroje</p>
          </div>
        </section>

        {/* Mission Card - style like app preview card */}
        <section className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-8 max-w-4xl mx-auto">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center flex-shrink-0">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Mūsų misija</h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Padėti kiekvienam lietuviui sėkmingai auginti ir prižiūrėti augalus, nepriklausomai nuo patirties lygio.
                  Mes tikime, kad su tinkamais įrankiais ir patarimais, kiekvienas gali turėti žaliuojantį sodą ar namus.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Timeline */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Mūsų istorija</h2>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">2023 metų pradžia</h3>
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        Idėjos gimimas
                      </span>
                    </div>
                    <p className="text-gray-600">
                      Kiemo Draugas gimė iš paprastos idėjos - padėti žmonėms geriau prižiūrėti savo augalus. Mūsų komanda
                      pastebėjo, kad daugelis lietuvių nori turėti gražų sodą, bet neturi pakankamai žinių.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">2023 metų vasara</h3>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                        Pirmieji vartotojai
                      </span>
                    </div>
                    <p className="text-gray-600">
                      Pirmieji 100 vartotojų prisijungė prie mūsų platformos. Gavome daug grįžtamojo ryšio ir supratome,
                      kad esame teisingu keliu. Pradėjome plėsti augalų duomenų bazę.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">2024 metai - šiandien</h3>
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        Augimas
                      </span>
                    </div>
                    <p className="text-gray-600">
                      Šiandien Kiemo Draugas yra patikimas partneris tūkstančiams lietuvių sodininkų. Mes nuolat
                      tobulėjame ir plečiame savo paslaugas, kad galėtume padėti dar daugiau žmonių.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Grid - using card style from dashboard preview */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Mūsų vertybės</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group">
              <div className="text-3xl mb-3">🌱</div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Paprastumas</h3>
              <p className="text-gray-600 text-sm">Augalų priežiūra neturi būti sudėtinga</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Pritaikomumas</h3>
              <p className="text-gray-600 text-sm">Kiekvienas augalas ir sodininkas yra unikalus</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group">
              <div className="text-3xl mb-3">🌍</div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Lokalumas</h3>
              <p className="text-gray-600 text-sm">Orientuojamės į Lietuvos klimatą</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group">
              <div className="text-3xl mb-3">💚</div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Tvarumas</h3>
              <p className="text-gray-600 text-sm">Skatiname ekologišką sodininkavimą</p>
            </div>
          </div>
        </section>

        {/* Team Section - using avatar style from user cards */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Mūsų komanda</h2>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                J
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Jurga Petrauskienė</h3>
              <p className="text-green-600 text-sm font-medium mb-3">Įkūrėja ir CEO</p>
              <p className="text-gray-600 text-sm">
                Botanikos magistrė su 15 metų patirtimi augalų priežiūroje. Jurga yra tikra augalų ekspertė.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                T
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Tomas Kazlauskas</h3>
              <p className="text-green-600 text-sm font-medium mb-3">Technologijų vadovas</p>
              <p className="text-gray-600 text-sm">
                Programuotojas su aistra gamtai. Tomas kuria technologijas, kurios padeda geriau suprasti augalų
                poreikius.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                I
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Ieva Mockutė</h3>
              <p className="text-green-600 text-sm font-medium mb-3">Dizaino vadovė</p>
              <p className="text-gray-600 text-sm">
                Kūrybinga dizainerė, kuri daro mūsų aplikaciją gražią ir patogią naudoti. Ieva tiki geru dizainu.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section - style like dashboard metrics */}
        <section id="stats-section" className="bg-white py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Mūsų pasiekimai</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center max-w-4xl mx-auto">
              <div className="p-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{counters.users.toLocaleString()}+</div>
                <div className="text-gray-600 text-sm">Aktyvių vartotojų</div>
              </div>

              <div className="p-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 mx-auto mb-4 flex items-center justify-center">
                  <Leaf className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{counters.plants.toLocaleString()}</div>
                <div className="text-gray-600 text-sm">Prižiūrimų augalų</div>
              </div>

              <div className="p-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 mx-auto mb-4 flex items-center justify-center">
                  <Database className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{counters.database.toLocaleString()}</div>
                <div className="text-gray-600 text-sm">Augalų duomenų bazėje</div>
              </div>

              <div className="p-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 mx-auto mb-4 flex items-center justify-center">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{counters.rating}/5</div>
                <div className="text-gray-600 text-sm">Vartotojų įvertinimas</div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Susisiekite su mumis</h2>
                <p className="text-gray-600 text-lg">Turite klausimų ar pasiūlymų? Mielai išgirsite iš jūsų!</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">El. paštas</p>
                      <p className="text-green-600">info@kiemodraugas.lt</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Telefonas</p>
                      <p className="text-green-600">+370 600 12345</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Adresas</p>
                      <p className="text-green-600">Vilnius, Lietuva</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input
                    placeholder="Jūsų vardas"
                    className="border-gray-200 focus:border-green-500 focus:ring-green-500"
                  />
                  <Input
                    type="email"
                    placeholder="El. pašto adresas"
                    className="border-gray-200 focus:border-green-500 focus:ring-green-500"
                  />
                  <Textarea
                    placeholder="Jūsų žinutė..."
                    rows={4}
                    className="border-gray-200 focus:border-green-500 focus:ring-green-500"
                  />
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3">
                    Siųsti žinutę
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutUs; 